import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { ImageType } from '../../../enums';
import { useHasAccess } from '../../../hooks/useHasAccess';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useCanViewLargeLicensePlate } from '../../../hooks/useAssetManagement';
import { Event, Metadata, MetadataLaneDataDetection } from '../../../interfaces';
import { getEventImageType, isContextCamera } from '../../../utils';
import { calculateContextCameraCoordinates, calculatePoint, clamp, getLicensePlateBounds } from '../../../utils/homographyHelpers';
import { getDetectionColor, routeLaneTypesToString } from './utils';

import { colorScheme } from '../../../theme';

export const CanvasWrapper = styled.div<{ isLPRPreview?: boolean; clickable?: boolean }>`
  ${({ isLPRPreview }) =>
    !isLPRPreview &&
    css`
      position: relative;
      overflow: hidden;
      aspect-ratio: 16 / 9;
      max-width: 100%;
      max-height: 100%;
      canvas {
        max-height: 100%;
        max-width: 100%;
        background: ${colorScheme.card.background};
        object-fit: contain;
      }
    `}
  ${({ isLPRPreview, clickable }) =>
    isLPRPreview &&
    css`
      cursor: ${clickable ? 'pointer' : 'default'};
    `}
`;
export interface zoomImageProps {
  src: string;
  event?: Event;
  drawLanes?: boolean;
  drawDetections?: boolean;
  drawDetectNet?: boolean;
  drawSegNet?: boolean;
  drawLprPov?: boolean;
  drawLPRDetection?: boolean;
  drawRoadEdges?: boolean;
  drawCarBox?: boolean;
  isLPRPreview?: boolean;
  disableZoom?: boolean;
  'data-accesskey'?: string;
  width?: number;
  height?: number;
  ariaLabel?: string;
  onContextMenu?: (e: React.SyntheticEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface TextSettings {
  color?: string;
  size?: number;
  align?: CanvasTextAlign;
}

export function ZoomableImage(props: zoomImageProps) {
  const SCROLL_SENSITIVITY = 0.0005;
  const MAX_ZOOM = 2;
  // Must stay strictly positive: the zoom value is fed into context.scale(),
  // and a non-positive scale would mirror or collapse the image.
  const MIN_ZOOM = 0.1;

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const hasAccess = useHasAccess();

  const touch = useRef({ x: 0, y: 0 });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLImageElement | null>(null);
  const [redrawTick, setRedrawTick] = useState(0);
  const [visibleLanes, _] = useLocalStorage('visibleLanes', []);

  const canViewLargeLP = useCanViewLargeLicensePlate();

  useEffect(() => {
    const img = new Image();
    img.src = props.src;
    img.onload = () => {
      if (!canvasRef.current) {
        return;
      }
      // Set canvas dimensions to match image. CSS and HTML adjust the <canvas so that it fits on screen.
      // We adjust the zoom / scale later inside the canvas object, but for starters everything should be 1:1
      // like its an img element in html
      canvasRef.current.width = img.width;
      canvasRef.current.height = img.height;
      backgroundRef.current = img;
      setRedrawTick((n) => n + 1);
    };
    backgroundRef.current = img;

    return () => {
      // If the image is still loading when we unmount, stop it and drop the
      // closure so the partially-fetched bytes can be released.
      img.onload = null;
      img.src = '';
      backgroundRef.current = null;
    };
  }, [props.src]);

  // Release the canvas backing store on unmount. The canvas is sized to the
  // image's full native resolution (see the load handler above), so a single
  // detached <canvas> retains width*height*4 bytes of GPU/heap memory until GC
  // runs. During long review sessions on low-RAM devices (e.g. Chromebooks)
  // these accumulate and drive the heap pressure seen in the portal telemetry.
  // Zeroing the dimensions frees the backing store immediately. Captured at
  // mount so the cleanup never races a detached ref.
  useEffect(() => {
    const canvas = canvasRef.current;
    return () => {
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    };
  }, []);

  const draw = () => {
    const background = backgroundRef.current;
    if (canvasRef.current && containerRef.current && background) {
      const { width, height } = canvasRef.current;
      const containerDimensions = containerRef.current?.getBoundingClientRect();

      const context = canvasRef.current.getContext('2d')!;

      // Set canvas dimensions
      canvasRef.current.width = width;
      canvasRef.current.height = height;

      const newScale = props.disableZoom ? 1 : zoom;

      // Clear canvas and scale it
      // Reset current transformation matrix to the identity matrix
      context.setTransform(1, 0, 0, 1, 0, 0);

      context.translate(-offset.x, -offset.y);
      context.scale(newScale, newScale);
      context.clearRect(0, 0, width, height);

      // Make sure we're zooming to the center
      const x = (context.canvas.width / newScale - background.width) / 2;
      const y = (context.canvas.height / newScale - background.height) / 2;

      // Draw image, which may actually not be on s3 or link can be expired
      try {
        context.drawImage(background, x, y);
      } catch (e) {
        console.error('Error drawing image', e);
      }

      const { event } = props;
      if (!event) {
        return;
      }
      const { metadata = {} } = event;

      // Draw Red Dot
      if (!props.drawLPRDetection) {
        drawRedDot(context, event, newScale, x, y, containerDimensions.width);
        if (hasAccess) {
          drawPinkDot(context, event, newScale, x, y, containerDimensions.width);
        }
      }
      if (props.drawDetections) {
        drawDetections(context, event, x, y);
      }
      if (props.drawDetectNet) {
        drawDetectNet(context, metadata, x, y);
      }
      if (props.drawSegNet) {
        drawSegNet(context, metadata, x, y);
      }
      if (props.drawLanes) {
        drawLanes(context, metadata, x, y);
      }
      if (props.drawLprPov) {
        drawLprPov(context, event, x, y);
      }
      if (props.drawLPRDetection && event.plate_crop) {
        drawPlateDetection(context, event.plate_crop, event.license_plate, x, y);
      }
      if (props.isLPRPreview && event.plate_crop) {
        drawPlateDetection(context, event.plate_crop, event.license_plate, x, y);
      }
      if (props.drawRoadEdges) {
        drawRoadEdges(context, metadata, x, y);
      }
      if (props.drawCarBox) {
        drawCarBox(context, event, x, y);
      }
    }
  };

  useEffect(draw, [
    redrawTick,
    zoom,
    offset,
    props.event?.metadata,
    props.drawLanes,
    props.drawDetections,
    props.drawDetectNet,
    props.drawSegNet,
    props.drawLprPov,
    props.drawLPRDetection,
    props.drawRoadEdges,
    props.drawCarBox,
    visibleLanes,
  ]);

  // This function returns the number between min and max values.
  // For example clamp(3,5,7) will return 5
  const handleWheel = (event: React.UIEvent<HTMLCanvasElement>) => {
    if (props.disableZoom) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();

    const { deltaY } = event as React.WheelEvent<HTMLCanvasElement>;
    setZoom((newZoom) => clamp(newZoom + deltaY * SCROLL_SENSITIVITY * -1, MIN_ZOOM, MAX_ZOOM));
    return false;
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (props.disableZoom) {
      return;
    }

    if (dragging) {
      const { x, y } = touch.current;
      const { clientX, clientY } = event;
      setOffset({
        x: offset.x + (x - clientX),
        y: offset.y + (y - clientY),
      });
      touch.current = { x: clientX, y: clientY };
    }
  };

  const handleMouseDown = (event: MouseEvent) => {
    const { clientX, clientY } = event;
    touch.current = { x: clientX, y: clientY };
    setDragging(true);
  };

  const handleMouseUp = () => setDragging(false);

  const drawPlateDetection = (ctx: CanvasRenderingContext2D, plateCrop: string, licensePlate: string, x: number, y: number) => {
    if (plateCrop && licensePlate) {
      const coords = plateCrop.split(';').map((v) => parseInt(v, 10));
      const coordsForPolygon: number[][] = [];
      coords.forEach((coord: number, i: number) => {
        if (i % 2) {
          coordsForPolygon[coordsForPolygon.length - 1][1] = coord;
        } else {
          coordsForPolygon.push([coord]);
        }
      });
      const position = coordsForPolygon.reduce((acc, val) => {
        return [Math.min(acc[0], val[0]), Math.min(acc[1], val[1])];
      }, coordsForPolygon[0]);
      position[1] -= 5;
      drawText(ctx, position, licensePlate, x, y);
      drawPolygon(ctx, coordsForPolygon, 'yellow', x, y);
    }
  };

  const drawText = (
    ctx: CanvasRenderingContext2D,
    position: number[],
    text: string,
    x: number,
    y: number,
    textSettings: TextSettings = {},
  ) => {
    const defaultTextSettings: TextSettings = {
      color: '#00FF00',
      size: 20,
      align: 'left',
    };
    textSettings = { ...defaultTextSettings, ...textSettings };
    ctx.font = `${textSettings.size}px Arial`;
    ctx.fillStyle = textSettings.color!;
    ctx.textAlign = textSettings.align!;
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1;
    ctx.strokeText(text, position[0] + x, position[1] + y);
    ctx.fillText(text, position[0] + x, position[1] + y);
  };

  const drawPolygon = (
    ctx: CanvasRenderingContext2D,
    polygonData: number[][],
    color = 'green',
    x: number,
    y: number,
    fillColor?: string,
    open = false,
  ) => {
    ctx.beginPath();
    polygonData.forEach((detectionCoordinates: number[]) => {
      ctx.lineTo(detectionCoordinates[0] + x, detectionCoordinates[1] + y);
    });
    if (!open) {
      ctx.closePath();
      if (fillColor) {
        ctx.fillStyle = fillColor;
        ctx.fill();
      }
    }
    ctx.strokeStyle = color;
    ctx.stroke();
  };

  const drawBox = (ctx: CanvasRenderingContext2D, box: number[], color: string, x: number, y: number) => {
    ctx.beginPath();
    ctx.rect(box[0] + x, box[1] + y, box[2] - box[0], box[3] - box[1]);
    ctx.strokeStyle = color;
    ctx.stroke();
  };

  const drawRedDot = (
    ctx: CanvasRenderingContext2D,
    event: Event,
    scale: number,
    translateX: number,
    translateY: number,
    containerWidth: number,
  ) => {
    const size = props.disableZoom ? 10000 / containerWidth : 10 / scale;
    const imageType = getEventImageType(event, hasAccess);
    const plateCropForImageType = event.plate_crops?.find(
      (pc) => pc.image_type === imageType || (imageType == ImageType.PreContext && pc.image_type == ImageType.PreContextRedDot),
    );
    let x = 0,
      y = 0;

    if (plateCropForImageType) {
      const crop = plateCropForImageType.plate_crop;
      switch (crop?.length) {
        case 2:
          x = crop[0];
          y = crop[1];
          break;
        // TODO: Handle other plate crop types.
        default:
          console.warn('Unsupported plate crop', { imageType, crop });
          return;
      }
    } else if (isContextCamera(event)) {
      if (!event.plate_crop) {
        return;
      }
      const bounds = getLicensePlateBounds(event.plate_crop);
      if (bounds.length < 4) {
        return;
      }
      x = (bounds[0].x + bounds[2].x) / 2;
      y = (bounds[0].y + bounds[3].y) / 2;
      y -= 4 * size;
    } else if (event.metadata?.homography && event.plate_crop) {
      const point = calculatePoint(event.metadata.homography, event.plate_crop);
      if (!point) {
        return;
      }
      x = point.x;
      y = point.y;
    } else {
      return;
    }

    drawCircle(ctx, x + translateX, y + translateY, size, 'red');
  };

  const drawPinkDot = (
    ctx: CanvasRenderingContext2D,
    event: Event,
    scale: number,
    translateX: number,
    translateY: number,
    containerWidth: number,
  ) => {
    const size = props.disableZoom ? 10000 / containerWidth : 10 / scale;

    const [x, y] = event.metadata?.car_detection?.estimated_plate_location || [-999, -999];

    drawCircle(ctx, x + translateX, y + translateY, size, 'pink');
  };

  const drawCircle = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string) => {
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI, true);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.closePath();
  };

  const drawDetections = (ctx: CanvasRenderingContext2D, event: Event, x: number, y: number) => {
    const { metadata } = event;
    const violatedDetectionId = metadata?.car_detection?.instance_id;
    if (metadata.lane_data?.detections?.length > 0) {
      metadata.lane_data.detections.forEach((detection: MetadataLaneDataDetection, i) => {
        const violationDetected = violatedDetectionId === detection.instance_id;
        const detectionColor = getDetectionColor(detection, violationDetected);
        if (violationDetected) {
          ctx.lineWidth = 3;
        }
        drawPolygon(ctx, detection.box, detectionColor, x, y);
        ctx.lineWidth = 1;
      });
    }
  };

  const drawDetectNet = (ctx: CanvasRenderingContext2D, metadata: Metadata, x: number, y: number) => {
    if (metadata.lane_data?.detectnet_poly?.length > 0) {
      drawPolygon(ctx, metadata.lane_data.detectnet_poly, 'yellow', x, y);
    }
  };

  const drawSegNet = (ctx: CanvasRenderingContext2D, metadata: Metadata, x: number, y: number) => {
    if (metadata.lane_data?.detectnet_poly?.length > 0) {
      drawPolygon(ctx, metadata.lane_data.segnet_poly, 'aqua', x, y);
    }
  };

  const drawCarBox = (ctx: CanvasRenderingContext2D, event: Event, x: number, y: number) => {
    const { metadata } = event;
    const { front_slice, rear_slice, front_slice_in_frame = 0, rear_slice_in_frame = 0 } = metadata?.car_detection || {};

    const frontInFrame = front_slice_in_frame >= 0.5;
    const rearInFrame = rear_slice_in_frame >= 0.5;
    const bothInFrame = frontInFrame && rearInFrame;

    const inFrameColor = '#00e676';
    const outOfFrameColor = '#888888';

    if (front_slice?.length === 4) {
      drawBox(ctx, front_slice, frontInFrame ? inFrameColor : outOfFrameColor, x, y);
    }

    if (rear_slice?.length === 4) {
      drawBox(ctx, rear_slice, rearInFrame ? inFrameColor : outOfFrameColor, x, y);
    }

    // connect all 4 corners of front and back if both present
    if (front_slice?.length === 4 && rear_slice?.length === 4) {
      ctx.beginPath();

      // TL
      ctx.moveTo(front_slice[0] + x, front_slice[1] + y);
      ctx.lineTo(rear_slice[0] + x, rear_slice[1] + y);

      // TR
      ctx.moveTo(front_slice[2] + x, front_slice[1] + y);
      ctx.lineTo(rear_slice[2] + x, rear_slice[1] + y);

      // BR
      ctx.moveTo(front_slice[2] + x, front_slice[3] + y);
      ctx.lineTo(rear_slice[2] + x, rear_slice[3] + y);

      // BL
      ctx.moveTo(front_slice[0] + x, front_slice[3] + y);
      ctx.lineTo(rear_slice[0] + x, rear_slice[3] + y);

      ctx.strokeStyle = bothInFrame ? inFrameColor : outOfFrameColor;
      ctx.stroke();
    }
  };

  const isLaneEnabled = (laneName: string) => {
    return visibleLanes.includes(laneName);
  };

  const drawRoadEdges = (ctx: CanvasRenderingContext2D, metadata: Metadata, x: number, y: number) => {
    if (metadata.lane_data?.keypoints) {
      const laneColorLeft = 'rgb(128, 0, 255)';
      const laneColorRight = 'rgb(255, 0, 128)';
      const size = 5;

      (metadata.lane_data.keypoints?.road_edge_left || []).forEach((point) => {
        drawCircle(ctx, point[0] + x, point[1] + y, size, laneColorLeft);
      });

      (metadata.lane_data.keypoints?.road_edge_right || []).forEach((point) => {
        drawCircle(ctx, point[0] + x, point[1] + y, size, laneColorRight);
      });
    }
  };

  const drawLanes = (ctx: CanvasRenderingContext2D, metadata: Metadata, x: number, y: number) => {
    if (metadata.lane_data?.lanes) {
      const selectedLaneNames = routeLaneTypesToString(metadata.route_metadata?.active_lane_selection || []);
      const laneNames = Object.keys(metadata.lane_data?.lanes)
        .filter((key) => !selectedLaneNames.includes(key))
        .concat(selectedLaneNames);
      laneNames.forEach((key: string) => {
        if (isLaneEnabled(key) && metadata.lane_data?.lanes[key]) {
          const isLaneEnforced = selectedLaneNames.includes(key);
          const laneColor = isLaneEnforced ? 'rgb(255, 0, 0)' : 'rgb(0, 255, 255)';
          const fillColor = isLaneEnforced ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 255, 255, 0.1)';
          metadata.lane_data.lanes[key].forEach((lane: number[][]) => {
            drawPolygon(ctx, lane, laneColor, x, y, fillColor);
          });
        }
      });
    }
  };

  const drawLprPov = (ctx: CanvasRenderingContext2D, event: Event, x: number, y: number) => {
    if (event?.metadata?.homography) {
      const poly = calculateContextCameraCoordinates(event.metadata.homography, event);
      drawPolygon(ctx, poly, 'blue', x, y);
    }
  };

  return (
    <CanvasWrapper
      data-accesskey={props['data-accesskey']}
      data-test="zoomable-image"
      ref={containerRef}
      isLPRPreview={props.isLPRPreview}
      aria-hidden
      clickable={canViewLargeLP}
      style={{
        width: props.width,
        height: props.height,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: props.width,
          height: props.height,
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onMouseMove={handleMouseMove}
        onContextMenu={props.onContextMenu}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        role={props.ariaLabel ? 'img' : undefined} // Add role="img" when we have a description
        aria-label={props.ariaLabel}
        alt={props.ariaLabel}
      />
    </CanvasWrapper>
  );
}
